import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import dog1 from './assets/image1.jpeg';

const AboutModal = ({ adventure, onClose }) => {
    const [isVisible, setIsVisible] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

    // 📱 Detect mobile on resize
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 🚫 Lock scroll visually but preserve position (no jump)
    useEffect(() => {
        const scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.body.style.overflowY = 'hidden';

        return () => {
            const storedScrollY = parseInt(document.body.style.top || '0', 10) * -1;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            document.body.style.overflowY = '';
            window.scrollTo(0, storedScrollY);
        };
    }, []);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(onClose, 400); // wait for animation to finish
    };

    return ReactDOM.createPortal(
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    key="aboutOverlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0,0,0,0.4)',
                        zIndex: 4000,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: isMobile ? '0' : '0',
                    }}
                    onClick={handleClose}
                >
                    <motion.div
                        key="aboutModal"
                        initial={{ x: '-100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ duration: 0.4, ease: 'easeInOut' }}
                        style={{
                            backgroundColor: '#D9D9D9',
                            width: isMobile ? '100vw' : '80vw',
                            height: isMobile ? '100dvh' : '80vh',
                            borderRadius: isMobile ? '0' : '20px',
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            overflow: 'hidden',
                            position: 'relative',
                            paddingTop: isMobile ? 'env(safe-area-inset-top)' : '0',
                            paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : '0',
                            boxSizing: 'border-box',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* ❌ Close Button — Fixed to modal */}
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            style={{
                                position: 'absolute',
                                top: isMobile ? '15px' : '20px',
                                right: isMobile ? '15px' : '20px',
                                cursor: 'pointer',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                width: isMobile ? '40px' : '45px',
                                height: isMobile ? '40px' : '45px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 3px 8px rgba(0,0,0,0.25)',
                                zIndex: 10,
                            }}
                            onClick={handleClose}
                        >
                            <X size={isMobile ? 22 : 28} color="#5FA89B" />
                        </motion.div>

                        {/* 🖼️ Image Section */}
                        <div
                            style={{
                                flex: isMobile ? '0 0 40%' : '0 0 40%',
                                height: isMobile ? '40vh' : '100%',
                                position: 'relative',
                                overflow: 'hidden',
                                borderTopLeftRadius: isMobile ? '0' : '20px',
                                borderBottomLeftRadius: isMobile ? '0' : '20px',
                            }}
                        >
                            <img
                                src={dog1}
                                alt="About Sadie"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                        </div>

                        {/* 📜 Scrollable Text Section */}
                        <div
                            style={{
                                flex: 1,
                                overflowY: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                padding: isMobile ? '40px 25px' : '80px 100px',
                                whiteSpace: 'pre-line',
                                position: 'relative',
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                backgroundColor: '#D9D9D9',
                            }}
                        >
                            <style>{`::-webkit-scrollbar { display: none; }`}</style>

                            <h2
                                style={{
                                    fontFamily: "'Libre Baskerville', serif",
                                    fontSize: isMobile ? '26px' : '42px',
                                    marginBottom: '40px',
                                    textAlign: 'center',
                                    lineHeight: 1.2,
                                    marginTop: isMobile ? '10px' : '0',
                                }}
                            >
                                About me...
                            </h2>

                            <p
                                style={{
                                    fontFamily: "'Libre Baskerville', serif",
                                    fontSize: isMobile ? '17px' : '20px',
                                    lineHeight: 1.6,
                                    textAlign: 'justify',
                                }}
                            >
                                {adventure.description}
                            </p>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default AboutModal;